import React, { useEffect, useState, useRef } from "react";
import santyClient from "../client.js";
import "../css/Projects.css";
import kmsScreenShot from "../images/KMS-site-screenshot.png";
import kmsDevScreenShot from "../images/KMS-dev-screenshot.png";

export default function Project() {
    const [projectData, setProjectData] = useState(null);
    const [expanded, setExpanded] = useState(null);

    // Create refs for each section
    const sectionRefs = useRef(['section-0', 'section-1', 'section-2'].map(() => React.createRef()));

    // Fetch project data from Sanity
    useEffect(() => {
        santyClient
            .fetch(`*[_type == "project"] | order(displayOrder asc){
                title,
                date,
                place,
                description,
                projectType,
                link,
                tags
            }`)
            .then((data) => setProjectData(data))
            .catch(console.error);
    }, []);

    // Use Intersection Observer to update the expanded state
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                let mostVisibleSection = null;
                let maxRatio = 0;
    
                // Determine the most visible section
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
                        maxRatio = entry.intersectionRatio;
                        mostVisibleSection = entry.target.getAttribute("id");
                    }
                });
    
                if (mostVisibleSection) {
                    const index = parseInt(mostVisibleSection.split("-")[1], 10);
                    if (expanded !== index) {
                        setExpanded(index);
                    }
                }
            },
            { threshold: Array.from({ length: 11 }, (_, i) => i / 10) } // Threshold steps from 0.0 to 1.0
        );
    
        sectionRefs.current.forEach((ref) => {
            if (ref.current) {
                observer.observe(ref.current);
            }
        });
    
        return () => {
            observer.disconnect(); // Cleanup observer
        };
    }, [expanded]);
    
    const container = document.querySelector(".projects-flex-continer");
    
    // Handle navigation click
    const handleExpand = (index, event) => {
        event.preventDefault();
        container.style.marginTop = "80px";
        
        setExpanded(index);
        const section = sectionRefs.current[index].current;
        if (section) {
            section.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <main className="projects-body">
         
            <div className="projects-flex-continer">
                

              
                <nav className="project-navigation">
                    <div>
                        <h1>Projects</h1>
                        <ul>
                            {["Web Development", "Audio Production", "Music"].map((topic, index) => (
                                <li key={index} onClick={(e) => handleExpand(index, e)}>
                                    <a
                                        href={`#section-${index}`}
                                        className={`topic-link ${expanded === index ? "expanded" : ""}`}
                                    >
                                        <span
                                            className={`topic-line-bullet ${
                                                expanded === index ? "expanded" : ""
                                            }`}
                                        ></span>
                                        <span>{topic}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </nav>
                <section className="project-display">
                    <h1 id="section-0" ref={sectionRefs.current[0]}>
                        Selected Projects
                    </h1>
                    <section>
                        {projectData &&
                            projectData.map((project, index) => (
                                <article className="project-card" key={index}>
                                    <h3>
                                        <a href={project.link} target="_blank" rel="noopener noreferrer">
                                            {project.title}
                                        </a>
                                    </h3>
                                    <div>
                                        <div>
                                            <strong>Finished on</strong>:{" "}
                                            {new Date(project.date).toLocaleDateString()}
                                        </div>
                                        <div>
                                            <strong>Created For</strong>: {project.place}
                                        </div>
                                        <div>
                                            <strong>Type</strong>: {project.projectType}
                                        </div>
                                        <p>{project.description}</p>
                                        <p>tags:</p>
                                        <div className="project-tags">
                                            {project.tags.map((tag, tagIndex) => (
                                                <span className="project-tag" key={tagIndex}>
                                                    {tag}{" "}
                                                </span>
                                            ))}
                                        </div>
                                        <a
                                            className="view-project-link"
                                            href={project.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View The Project{" "}
                                        </a>
                                    </div>
                                </article>
                            ))}
                        <article className="project-card">
                            <h3>Web Development</h3>
                            <div>
                                <a
                                    href="https://www.kmsoundstrategies.com/dev/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img src={kmsDevScreenShot} alt="Web Development" />
                                </a>
                            </div>
                        </article>
                    </section>
                    <h1 id="section-1" ref={sectionRefs.current[1]}>
                        Audio Production
                    </h1>
                    <article className="project-card">
                        <div>
                            <a
                                href="https://www.kmsoundstrategies.com/audio/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img src={kmsScreenShot} alt="Audio Production" />
                            </a>
                        </div>
                    </article>
                    
                    <h1 id="section-2" ref={sectionRefs.current[2]}>
                        Music
                    </h1>
                    <article className="project-card">
                        <h3>Music</h3>
                        <div className="spotify-embed">
                            <iframe
                                style={{ borderRadius: "12px" }}
                                src="https://open.spotify.com/embed/artist/59NZnTwKKpwutpAllvDQRQ?utm_source=generator&theme=0"
                                width="100%"
                                height="352"
                                frameBorder="0"
                                allowFullScreen=""
                                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                loading="lazy"
                            ></iframe>
                        </div>
                    </article>
                    <article className="project-card">
                        {/* spotify music for media embed */}
                        <div>
                            <h3>music and sound design for media</h3>
                            <div>
                                <iframe
                                    width="100%"
                                    height="450"
                                    scrolling="no"
                                    frameBorder="no"
                                    allow="autoplay; encrypted-media"
                                    src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/29102416&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true"
                                ></iframe>
                                <div
                                    style={{
                                        fontSize: "15px",
                                        color: "#cccccc",
                                        lineBreak: "anywhere",
                                        wordBreak: "normal",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        fontFamily:
                                            "Interstate, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Garuda, Verdana, Tahoma, sans-serif",
                                        fontWeight: 100,
                                    }}
                                >
                                    <a
                                        href="https://soundcloud.com/kane-mathis"
                                        title="Kane Mathis"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#cccccc", textDecoration: "none" }}
                                    >
                                        Kane Mathis
                                    </a>{" "}
                                    ·{" "}
                                    <a
                                        href="https://soundcloud.com/kane-mathis/sets/recent-commissions"
                                        title="Composition for Media"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#cccccc", textDecoration: "none" }}
                                    >
                                        Composition for Media
                                    </a>
                                </div>
                            </div>
                        </div>
                    </article>
                </section>
                </div>
         
        </main>
    );
}
